import TitleStore from "../store/title-store";
import {Title} from "../../entity/model/title";
import {PageOption} from "../../entity/enums/page-option";
import {toast} from "react-toastify";
import JSZip from "jszip";
import {saveAs} from "file-saver";
import {TitleTemplateRequest} from "../../entity/resource/title-template-request";
import {State} from "../../entity/model/state";
import {County} from "../../entity/model/county";
import * as ExcelJS from 'exceljs';
import {TemplateType} from "../../entity/enums/template-type";
import {TitleDialogDto} from "../../entity/dto/title-dialog-dto";
import {ReviewStatus} from "../../entity/enums/review-status";
import {SplitAIRequestDto} from "../../entity/dto/split-ai-request-dto";
import {Project} from "../../entity/model/project";
import {RequestBody} from "../../entity/core/request-body";
import {CommentDto} from "../../entity/dto/comment-dto";

export default class TitleService {
    titleStore: TitleStore;

    constructor(titleStore: TitleStore) {
        this.titleStore = titleStore;
    }

    getTitles = async (organizationId: string, projectId: string): Promise<Title[]> => {
        return await this.titleStore.getTitles(organizationId, projectId);
    }

    getTitleById = async (organizationId: string, titleId: string): Promise<Title | undefined> => {
        return await this.titleStore.getTitleById(organizationId, titleId);
    }

    addNewSection = async (organizationId: string, titleId: string, sections: string[]): Promise<Title | undefined> => {
        if (sections.length < 1) {
            toast.warning('Please select at least one section !!');
            return;
        }

        const body = sections.map((section: string) => ({ name: section }));
        const response = await this.titleStore.addNewSection(organizationId, titleId, {body});

        if (typeof response == 'string') {
            toast.error(response);
            return;
        } else {
            toast.success('New section added successfully !!');
            return response;
        }
    }

    getTitle = (): Title | undefined => {
        return this.titleStore.selectedTitle;
    }

    setTitle = (title: Title): void => {
        this.titleStore.selectedTitle = title;
    }

    getCache = (): boolean => {
        return this.titleStore.cache;
    }

    setCache = (status: boolean): void => {
        this.titleStore.cache = status;
        this.titleStore.loading = false;
    }

    isLoading = (): boolean => {
        return this.titleStore.loading;
    }

    getPageOptions = (): PageOption[] => {
        return [PageOption.DOCUMENTS, PageOption.VALIDATE_DOCUMENTS, PageOption.DOCUMENT_RUNNER, PageOption.EXPORT, PageOption.DOWNLOAD, PageOption.IMPORT, PageOption.INVOICES, PageOption.REPORTS, PageOption.LINE_LIST_REPORTS];
    }

    getPageOption = (): PageOption => {
        return this.titleStore.pageOption;
    }

    setPageOption = (option: PageOption): void => {
        this.titleStore.pageOption = option;
    }

    downloadTitle = async (selectedSections: Map<string, string[]>, titleTemplateRequests: TitleTemplateRequest[], states: State[], counties: County[]): Promise<void> => {
        if(titleTemplateRequests.length === 0){
            toast.warning('Please select at least one title template !!');
            return;
        }

        const zip = new JSZip();

        for(const titleTemplateRequest of titleTemplateRequests) {
            const templateFolder = zip.folder(titleTemplateRequest.templateName);
            if (!templateFolder) {
                console.error('Failed to create ' + titleTemplateRequest.templateName + ' folder in the ZIP.');
                return;
            }

            for (const section of titleTemplateRequest.sections) {
                if (titleTemplateRequests.length > 1) {
                    const sectionFolder = templateFolder.folder(section.name.replace(/[\/\\:*?"<>|]/g, '_'));
                    if (!sectionFolder) continue;

                    for (const document of section.documents) {
                        const response = await fetch(document.url);
                        if (!response.ok) {
                            console.error(`Failed to fetch ${document.url}`);
                            continue;
                        }
                        const blob = await response.blob();
                        sectionFolder.file(document.name.endsWith('.pdf') ? document.name : document.name + '.pdf', blob);
                    }
                } else {
                    if (!selectedSections.has(section.id)) continue;

                    const sectionFolder = templateFolder.folder(section.name.replace(/[\/\\:*?"<>|]/g, '_'));
                    if (!sectionFolder) continue;

                    const documentIds = selectedSections.get(section.id) || [];
                    for (const document of section.documents) {
                        if (!documentIds.includes(document.id)) continue;

                        const response = await fetch(document.url);
                        if (!response.ok) {
                            console.error(`Failed to fetch ${document.url}`);
                            continue;
                        }
                        const blob = await response.blob();
                        sectionFolder.file(document.name.endsWith('.pdf') ? document.name : document.name + '.pdf', blob);
                    }
                }
            }
        }

        const excelBlob = await this.downloadExcel(titleTemplateRequests, states.map(state => state.shortName), counties.map(county => county.name));
        zip.file(`template.xlsx`, excelBlob);

        const zipBlob = await zip.generateAsync({ type: 'blob' });
        saveAs(zipBlob, 'Titles.zip');

        toast.success('Title template downloaded successfully !!');
    }

    downloadExcel = async (titleTemplateRequests: TitleTemplateRequest[], stateOptions: string[], countyOptions: string[]): Promise<Blob> => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('Data');

        sheet.columns = [
            {header: 'Template Name', key: 'templateName', width: 30},
            {header: 'Template Type', key: 'templateType', width: 30},
            {header: 'TMS Numbers', key: 'tmsNumbers', width: 30},
            {header: 'State', key: 'state', width: 30},
            {header: 'County', key: 'county', width: 30},
            {header: 'Address', key: 'address', width: 30},
            {header: 'Subdivision', key: 'subdivision', width: 30}
        ];

        const stateValidation: ExcelJS.DataValidation = {
            type: 'list',
            formulae: [`"${stateOptions.join(',')}"`],
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please select a valid state.',
        };

        countyOptions = countyOptions.length > 10 ? countyOptions.slice(0, 10) : countyOptions;
        const countyValidation: ExcelJS.DataValidation = {
            type: 'list',
            formulae: [`"${countyOptions.join(',')}"`],
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please select a valid county.',
        };

        const templateTypeOptions = Object.values(TemplateType);
        const templateTypeValidation: ExcelJS.DataValidation = {
            type: 'list',
            formulae: [`"${templateTypeOptions.join(',')}"`],
            showErrorMessage: true,
            errorTitle: 'Invalid Input',
            error: 'Please select a valid template type',
        };

        for (let row = 2; row <= titleTemplateRequests.length + 1; row++) {
            sheet.getCell(`B${row}`).dataValidation = templateTypeValidation;
            sheet.getCell(`D${row}`).dataValidation = stateValidation;
            sheet.getCell(`E${row}`).dataValidation = countyValidation;
        }

        for (let index= 0; index < titleTemplateRequests.length; index++)
            sheet.insertRow(2 + index, [
                titleTemplateRequests[index].templateName + '_Duplicated',
                titleTemplateRequests[index].templateType,
                titleTemplateRequests[index].tmsNumbers || '',
                titleTemplateRequests[index].state || '',
                titleTemplateRequests[index].county || '',
                titleTemplateRequests[index].address || '',
                titleTemplateRequests[index].subdivision || ''
            ]);

        const buffer = await workbook.xlsx.writeBuffer();
        return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    }

    getTitleDialogDto = (): TitleDialogDto => {
        return {
            comment: '',
            reviewStatus: ReviewStatus.APPROVE
        }
    }

    getSplitAIRequestDto = (): SplitAIRequestDto => {
        return {
            model: ''
        };
    }

    setRefreshTitleDetails = (status: boolean): void => {
        this.titleStore.refreshTitleDetails = status;
    }

    isRefreshTitleDetails = (): boolean => {
        return this.titleStore.refreshTitleDetails;
    }

    addComment = async (organizationId: string, titleId: string, comment: string): Promise<Title | undefined> => {
        const requestBody: RequestBody = {body: {comment: comment}};
        const response = await this.titleStore.addComment(organizationId, titleId, requestBody);

        if (typeof response !== 'string') {
            toast.success('Comments added successfully !!');
            return response;
        } else
            toast.error(response);
    }

    getCommentDto = (): CommentDto => {
        return {
            comment: '',
        };
    }

    submitTitle = async (organizationId: string, titleId: string, comment: string): Promise<Title | undefined> => {
        if (comment.length > 0) {
            const requestBody: RequestBody = {body: {comment: comment}};
            await this.titleStore.addComment(organizationId, titleId, requestBody);
        }

        const response = await this.titleStore.submitTitle(organizationId, titleId);
        if (typeof response === 'string')
            toast.error(response);
        else {
            toast.success('Title submitted for review !!');
            return response;
        }
    }

    reviewTitle = async (organizationId: string, titleId: string, comment: string, reviewStatus: ReviewStatus): Promise<Title | undefined> => {
        if (comment.length > 0) {
            const requestBody: RequestBody = {body: {comment: comment}};
            await this.titleStore.addComment(organizationId, titleId, requestBody);
        }

        const response = await this.titleStore.reviewTitle(organizationId, titleId, reviewStatus);
        if (typeof response === 'string')
            toast.error(response);
        else {
            toast.success('Title review successful !!');
            return response;
        }
    }

    approveTitle = async (organizationId: string, titleId: string, comment: string, reviewStatus: ReviewStatus): Promise<Title | undefined> => {
        if (comment.length > 0) {
            const requestBody: RequestBody = {body: {comment: comment}};
            await this.titleStore.addComment(organizationId, titleId, requestBody);
        }

        const response = await this.titleStore.approveTitle(organizationId, titleId, reviewStatus);
        if (typeof response === 'string')
            toast.error(response);
        else {
            toast.success('Title approved successfully !!');
            return response;
        }
    }
}
